/**
 * Feature E08-F01 — "MS Graph OAuth2 + email send/read" request/response
 * contracts (E08-F01-T02 / T03 / T05 / T06 / T09 / T10 / T11).
 *
 * 1:1 port of the legacy n8n Users group (docs/source-n8n/reference/Users.md →
 * `save_oauth_token`, `disconnect_outlook`, `get_outlook_status`) and the
 * standalone OAuth callback (`../EER app/public/oauth/callback/index.php`).
 *
 * Single source of truth for:
 *   - GET  /outlook/authorize-url   build the Microsoft /organizations authorize URL  (T02)
 *   - GET  /oauth/callback          exchange code → tokens, save, postMessage         (T02/T03/T09)
 *   - GET  /outlook/status          live Outlook connection status for the session    (T11)
 *   - POST /outlook/disconnect      soft-deactivate the oauth mailbox row             (T10)
 *
 * Multi-tenant invariant (callback PHP comments, T02/T04/T09): the authorize and
 * refresh endpoints use the `/organizations/` tenant, NEVER a fixed tenant — the
 * app is registered as Multitenant and Microsoft routes by the auth code /
 * refresh token itself. The real tenant is recovered from the access_token `tid`
 * claim and stored as `oauth_tenant_id`.
 *
 * Email-match invariant (Users.md `save_oauth_token`, T03): the Microsoft
 * `outlook_email` MUST equal (case-insensitive) the system `users.email`, else
 * the tokens are rejected — a recruiter can only bind their OWN mailbox.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Constants (verbatim from the legacy flow — DO NOT drift)
// ---------------------------------------------------------------------------
/**
 * OAuth scope — EXACTLY this string, space-separated, same at authorize AND
 * refresh (callback PHP `scope` + dashboard.js; T02/T04 acceptance).
 */
export const OUTLOOK_OAUTH_SCOPE = 'Mail.Read Mail.Send User.Read offline_access';
/** Multi-tenant authorize endpoint (`/organizations/`, NOT a fixed tenant). */
export const OUTLOOK_AUTHORIZE_ENDPOINT = 'https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize';
/** Multi-tenant token endpoint (code exchange + refresh both hit this). */
export const OUTLOOK_TOKEN_ENDPOINT = 'https://login.microsoftonline.com/organizations/oauth2/v2.0/token';
/** Microsoft Graph profile endpoint (recover the connected mailbox email). */
export const GRAPH_ME_URL = 'https://graph.microsoft.com/v1.0/me';
/** Microsoft Graph messages endpoint (read inbox). */
export const GRAPH_MESSAGES_URL = 'https://graph.microsoft.com/v1.0/me/messages';
/** Microsoft Graph sendMail endpoint. */
export const GRAPH_SENDMAIL_URL = 'https://graph.microsoft.com/v1.0/me/sendMail';
/**
 * OAuth state freshness window (seconds). The callback rejects a state whose
 * `ts` is more than this old (callback PHP `abs(time() - $ts) > 900`; T02).
 */
export const OUTLOOK_STATE_MAX_AGE_SECONDS = 900;
/** Graph read page size (callback / Att flow `$top=50`; T05). */
export const GRAPH_READ_TOP = 50;
// ---------------------------------------------------------------------------
// Admin-consent (multi-tenant onboarding of additional domains — T09)
// ---------------------------------------------------------------------------
/**
 * Per-tenant admin-consent endpoint base (T09). The app is registered
 * Multitenant, so recruiters from an ADDITIONAL domain can only connect after a
 * tenant admin grants consent ONCE for that tenant. The admin opens:
 *
 *   https://login.microsoftonline.com/{tenant|domain}/adminconsent
 *     ?client_id=<AZURE_CLIENT_ID>&redirect_uri=<…/oauth/callback>
 *
 * Microsoft then redirects back to redirect_uri with `?admin_consent=True&tenant=…`
 * (the callback shows a success screen and does NOT exchange a code — there is no
 * `code` on this branch). Build the URL with {@link buildAdminConsentUrl}.
 */
export const OUTLOOK_ADMIN_CONSENT_BASE = 'https://login.microsoftonline.com';
/**
 * Build the per-tenant admin-consent URL (T09). `tenant` is the tenant id or a
 * verified domain (e.g. `contoso.com`); `clientId` is the Azure app id;
 * `redirectUri` MUST be a redirect URI registered on the app (the same
 * `/oauth/callback` the OAuth flow uses — the callback recognises the resulting
 * `?admin_consent=True`).
 */
export function buildAdminConsentUrl(args) {
    // Build the query manually (the shared package compiles with the ES-only lib,
    // so `URLSearchParams` is not in scope here).
    const query = `client_id=${encodeURIComponent(args.clientId)}` +
        `&redirect_uri=${encodeURIComponent(args.redirectUri)}`;
    return `${OUTLOOK_ADMIN_CONSENT_BASE}/${encodeURIComponent(args.tenant)}/adminconsent?${query}`;
}
/**
 * True when a callback query is the admin-consent SUCCESS branch (T09): Microsoft
 * sends `admin_consent=True` (case-insensitive) and NO authorization `code`. The
 * callback must show success WITHOUT attempting a code exchange — a render hiccup
 * on this branch is not a consent failure (T09 acceptance #1/#4).
 */
export function isAdminConsentCallback(query) {
    const flag = (query.admin_consent ?? '').toLowerCase();
    return flag === 'true' && !query.code;
}
// ---------------------------------------------------------------------------
// OAuth state (base64(JSON{ user_id, ts, st })) — callback PHP / dashboard.js
// ---------------------------------------------------------------------------
/**
 * Decoded OAuth `state`: `base64(JSON{ user_id, ts, st })` where `st` is the
 * session token (dashboard.js `connectOutlook` builds it; callback PHP decodes
 * and validates it). `ts` is a UNIX-seconds timestamp.
 */
export const outlookOAuthStateSchema = z.object({
    /** The system user id this connection is for (callback derives ownership). */
    user_id: z.string().min(1),
    /** UNIX seconds the state was minted (freshness check, ≤ 900s). */
    ts: z.number().int(),
    /** Session token (carried through so save can authenticate). Optional. */
    st: z.string().optional(),
});
// ---------------------------------------------------------------------------
// GET /outlook/authorize-url — build the Microsoft authorize URL (T02)
// ---------------------------------------------------------------------------
/** Response of the authorize-url builder — the full URL the popup opens. */
export const authorizeUrlResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    /** The fully-built `…/organizations/…/authorize?…` URL. */
    authorize_url: z.string().url(),
    /** The redirect_uri baked into the URL (echoed for the SPA to verify). */
    redirect_uri: z.string(),
});
// ---------------------------------------------------------------------------
// save_oauth_token — tokens persisted after a successful code exchange (T03)
// ---------------------------------------------------------------------------
/**
 * The payload the callback hands to the save step (Users.md `save_oauth_token`
 * inputs). `userId` + `outlook_email` drive the email-match check; the rest are
 * persisted into the `mailbox_accounts` oauth row.
 */
export const saveOauthTokenInputSchema = z.object({
    userId: z.string().min(1),
    outlook_email: z.string().min(1),
    oauth_access_token: z.string().min(1),
    oauth_refresh_token: z.string().nullable().optional(),
    /** ISO/`Y-m-d H:i:s` expiry (now + expires_in). */
    oauth_token_expires_at: z.string().min(1),
    oauth_scope: z.string().optional(),
    /** `tid` claim from the access token (nullable — '' allowed per T02). */
    oauth_tenant_id: z.string().nullable().optional(),
});
/** Success body of the save step (Users.md `Return OAuth OK`). */
export const saveOauthTokenResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.literal('OAuth tokens saved'),
});
// ---------------------------------------------------------------------------
// GET /outlook/status — live connection status (Users.md get_outlook_status, T11)
// ---------------------------------------------------------------------------
/**
 * Outlook connection status for the session user (Users.md "Return Outlook
 * Status"): `has_outlook` + the connected `outlook_email`, derived from
 * `mailbox_accounts` WHERE auth_type='oauth' AND is_active=true.
 */
export const outlookStatusResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    has_outlook: z.boolean(),
    outlook_email: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// POST /outlook/disconnect — soft-deactivate (Users.md disconnect_outlook, T10)
// ---------------------------------------------------------------------------
/**
 * Disconnect input. `userId` optional — admins disconnect OTHERS by id, a
 * recruiter disconnects their own (the route falls back to the session id when
 * omitted; role-gating for the cross-user case is enforced server-side).
 */
export const disconnectOutlookInputSchema = z.object({
    userId: z.string().min(1).optional(),
});
/** Disconnect success body (Users.md "Return Disconnect OK"). */
export const disconnectOutlookResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.literal('Outlook disconnected'),
});
// ---------------------------------------------------------------------------
// Graph read mapping — one inbound email (T05)
// ---------------------------------------------------------------------------
/**
 * Internal email shape produced by mapping a Graph `/me/messages` item
 * (Users/Att flow; T05 acceptance mapping). `direction` is always `'in'` for
 * read messages; `external_id`=internetMessageId, `thread_id`=conversationId.
 */
export const graphMappedEmailSchema = z.object({
    external_id: z.string(),
    thread_id: z.string().nullable(),
    sender: z.string(),
    sender_name: z.string().nullable(),
    recipient: z.string().nullable(),
    subject: z.string().nullable(),
    body: z.string(),
    received_at: z.string(),
    has_attachments: z.boolean(),
    direction: z.literal('in'),
});
//# sourceMappingURL=outlook-oauth.js.map